import {
  createSlice,
  createAction,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'
import {
  EndpointsConfigTypes,
  ImportGroupsType,
  ImportUsersType,
  ListBlockPages,
  ListInvitedAdminsTypes,
  SliceStatus,
  UpdateCustomBlockPages,
  UsersAndGroupsLastEvaluatedKeyTypes,
  DefaultBlockPagePayload,
  SearchUsersGroupsItem,
  BlockPageList,
  UserGroupDetailsState,
  EndpointConfigState,
  UserEntity,
  GroupEntity,
  GetImportStatusResponse,
  ImportLinkState,
  Roles,
  SettingThrowError,
  UserImportStatus,
  AUTH_PROVIDER,
} from 'interfaces'
import {
  getLink,
  getLinkFailure,
  getLinkSuccess,
  getGroupsUsers,
  getGroupsUsersFailure,
  getGroupsUsersSuccess,
  getImportStatus,
  getImportStatusFailure,
  getImportStatusSuccess,
  getSearchUsersGroups,
  getSearchUsersGroupsFailure,
  getSearchUsersGroupsSuccess,
  resetSearch,
  getGroups,
  getGroupsFailure,
  getGroupsSuccess,
  getUsers,
  getUsersFailure,
  getUsersSuccess,
  resetUsersGroups,
  setShowUserGroupDetails,
  getUserGroupDetails,
  getUserGroupDetailsSuccess,
  getUserGroupDetailsFailure,
} from './Users/userActions'

import {
  inviteAdmins,
  inviteAdminsFailure,
  inviteAdminsSuccess,
  deleteAdmin,
  deleteAdminFailure,
  deleteAdminSuccess,
  updateInvitedAdmins,
  updateInvitedAdminsFailure,
  updateInvitedAdminsSuccess,
  getRoles,
  getRolesFailure,
  getRolesSuccess,
} from './General/generalActions'

import {
  getBlockPagesSuccess,
  defaultBlockPagesUpdate,
  defaultBlockPagesUpdateFailure,
  defaultBlockPagesUpdateSuccess,
  resetDefaultBlockPagesUpdate,
  customBlockPagesUpdate,
  customBlockPagesUpdateFailure,
  customBlockPagesUpdateSuccess,
  resetCustomBlockPagesUpdate,
  updateDefaultBlockPagePayload,
  updateSettingField,
  resetSettingUpdateField,
  formSubmitted,
  savePressed,
} from './BlockPages/blockPagesActions'

import {
  getEndpointsConfig,
  getEndpointsConfigFailure,
  getEndpointsConfigSuccess,
  setEndpointConfigFormValidation,
  setShowEndpointConfigView,
  updateEndpointsConfig,
  updateEndpointsConfigFailure,
  updateEndpointsConfigSuccess,
} from './Endpoints/endpointsActions'

import isEqual from 'lodash.isequal'
import {categoryState} from 'definitions/constant'

const changeEndpointsConfig = createAction<EndpointsConfigTypes>(
  'changeEndpointsConfig',
)
const removeEndpointsConfigSnapshot = createAction(
  'removeEndpointsConfigSnapshot',
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changeBlockPages = createAction<any>('changeBlockPages')
const removeBlockPagesSnapshot = createAction('removeBlockPagesSnapshot')
const updateCustomBlockPageData = createAction<UpdateCustomBlockPages>(
  'updateCustomBlockPage',
)
const removeUpdatedCustomBlockPageData = createAction(
  'removeUpdatedCustomBlockPageData',
)

const startPollingGroupsUsers = createAction('startPollingGroupsUsers')
const stopPollingGroupsUsers = createAction('stopPollingGroupsUsers')

const resetImportStatus = createAction('resetImportStatus')
const resetEndpointsConfig = createAction('resetEndpointsConfig')

const updateSliceStatus = createAction<SliceStatus>('updateSliceStatus')

type SettingsSliceData = {
  link: ImportLinkState
  userGroupDetails: UserGroupDetailsState
  users: ImportUsersType
  groups: ImportGroupsType
  invitedAdmins: {
    data: ListInvitedAdminsTypes[]
    status: SliceStatus
  }
  usersGroupsList: SearchUsersGroupsItem[]
  // blockPages: ListBlockPages,
  blockPages: {
    data: ListBlockPages
    snapshot: ListBlockPages
    updatedData: UpdateCustomBlockPages
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blockPageList: any[]
  }
  updateDefaultBlockPagesStatus: SliceStatus
  updateCustomBlockPageStatus: SliceStatus
  defaultBlockPagePayload: DefaultBlockPagePayload
  endpointsConfig: EndpointConfigState
  status: SliceStatus
  pollingGroupsUsers: boolean
  error: string
  importStatus: {
    status: SliceStatus
    data: GetImportStatusResponse
  }
  availableRoles: Roles[]
  rolesStatus: SliceStatus
  updateInvitedAdminsStatus: SliceStatus
  updatedSettingField?: SettingThrowError
  settingReverted: boolean
  savePressed: boolean
  formSubmitted: boolean
}

const userGroupDetailsInitState: UserGroupDetailsState = {
  show: false,
  name: '',
  emailId: '',
  groupIds: [],
  groupNames: [],
  status: SliceStatus.idle,
  error: '',
}

const usersListInitialState: ImportUsersType = {
  message: '',
  Items: [] as UserEntity[],
  LastEvaluatedKey: {} as UsersAndGroupsLastEvaluatedKeyTypes,
  hasMore: true,
  status: SliceStatus.idle,
}

const groupsListInitialState: ImportGroupsType = {
  message: '',
  Items: [] as GroupEntity[],
  LastEvaluatedKey: {} as UsersAndGroupsLastEvaluatedKeyTypes,
  hasMore: true,
  status: SliceStatus.idle,
}

const importStatusInitialState = {
  status: SliceStatus.idle,
  data: {
    authProvider: '' as AUTH_PROVIDER,
    importStatus: UserImportStatus.NOT_STARTED,
    lastUpdated: '',
    reauthRequired: false,
  },
}

const importLinkInitialState = {
  url: '',
  message: '',
  notifier_type: '',
  sns_topic: '',
  status: SliceStatus.idle,
}

const endpointsConfigInitState = {
  data: {} as EndpointsConfigTypes,
  snapshot: {} as EndpointsConfigTypes,
  status: SliceStatus.idle,
  isFormValid: false,
  showConfigView: false,
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    link: importLinkInitialState,
    importStatus: importStatusInitialState,
    userGroupDetails: userGroupDetailsInitState,
    users: usersListInitialState,
    groups: groupsListInitialState,
    invitedAdmins: {
      data: {} as ListInvitedAdminsTypes[],
      status: SliceStatus.idle,
    },
    usersGroupsList: [] as SearchUsersGroupsItem[],
    // blockPages: {} as ListBlockPages,
    blockPages: {
      data: {} as ListBlockPages,
      snapshot: {} as ListBlockPages,
      updatedData: {} as UpdateCustomBlockPages,
      blockPageList: [] as BlockPageList[],
      status: SliceStatus.idle,
    },
    defaultBlockPagePayload: {} as DefaultBlockPagePayload,
    updateDefaultBlockPagesStatus: SliceStatus.idle,
    updateCustomBlockPageStatus: SliceStatus.idle,
    endpointsConfig: endpointsConfigInitState,
    pollingGroupsUsers: false,
    status: SliceStatus.idle,
    error: '',
    availableRoles: [] as Roles[],
    rolesStatus: SliceStatus.idle,
    updateInvitedAdminsStatus: SliceStatus.idle,
    updatedSettingField: {} as SettingThrowError,
    settingReverted: false,
    savePressed: false,
    formSubmitted: false,
  },
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<SettingsSliceData>) =>
    builder
      .addCase(getLink, state => ({
        ...state,
        status: SliceStatus.pending,
        link: {
          ...state.link,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getLinkFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
        link: {
          ...state.link,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(getLinkSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        link: {...action.payload, status: SliceStatus.resolved},
      }))
      .addCase(getImportStatus, state => ({
        ...state,
        importStatus: {...state.importStatus, status: SliceStatus.pending},
      }))
      .addCase(getImportStatusFailure, state => ({
        ...state,
        importStatus: {...state.importStatus, status: SliceStatus.rejected},
      }))
      .addCase(getImportStatusSuccess, (state, action) => ({
        ...state,
        importStatus: {
          ...state.importStatus,
          status: SliceStatus.resolved,
          data: {...action.payload},
        },
      }))
      .addCase(resetImportStatus, state => ({
        ...state,
        importStatus: {...importStatusInitialState},
      }))
      .addCase(getGroupsUsers, state => ({
        ...state,
        status: SliceStatus.pending,
        users: {...usersListInitialState, status: SliceStatus.pending},
        groups: {...groupsListInitialState, status: SliceStatus.pending},
      }))
      .addCase(getGroupsUsersFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
        users: {...state.users, status: SliceStatus.rejected},
        groups: {...state.groups, status: SliceStatus.rejected},
      }))
      .addCase(getGroupsUsersSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        importStatus: {
          ...state.importStatus,
          data: action.payload.importStatus,
          status: SliceStatus.resolved,
        },
        groups: {
          ...action.payload,
          message: 'Success',
          Items: [
            ...state?.groups?.Items,
            ...(action.payload?.groups.body?.Items ?? []),
          ],
          LastEvaluatedKey: action.payload?.groups.body?.LastEvaluatedKey ?? {},
          hasMore:
            action.payload.groups.body &&
            'LastEvaluatedKey' in action.payload.groups.body &&
            action.payload.groups.body.LastEvaluatedKey !== null,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(startPollingGroupsUsers, state => ({
        ...state,
        pollingGroupsUsers: true,
      }))
      .addCase(stopPollingGroupsUsers, state => ({
        ...state,
        pollingGroupsUsers: true,
      }))
      .addCase(getSearchUsersGroups, state => ({
        ...state,
        status: SliceStatus.pending,
        usersGroupsList: [],
      }))
      .addCase(getSearchUsersGroupsFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
      }))
      .addCase(getSearchUsersGroupsSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        usersGroupsList: action.payload,
      }))
      .addCase(resetSearch, state => ({
        ...state,
        usersGroupsList: [] as SearchUsersGroupsItem[],
      }))
      .addCase(getBlockPagesSuccess, (state, action) => {
        // Create a safe reference to the Item and pages
        const item = action.payload?.Item ?? {}
        const pages = item?.pages ?? {}

        // Safely extract and combine the arrays
        const dopeDefault = Array.isArray(pages?.dope_default)
          ? pages.dope_default
          : []
        const custom = Array.isArray(pages?.custom) ? pages.custom : []

        // Combine the arrays and map safely
        const blockPageList = [...dopeDefault, ...custom].map(ele => ({
          label: ele.label ?? '',
          value: ele.pageId ?? '',
          type:
            (ele.type && ele.type === '1') ||
            ele.label === categoryState.Blocked
              ? '1'
              : '2',
        }))

        return {
          ...state,
          blockPages: {
            ...state.blockPages,
            data: item,
            blockPageList,
            snapshot: item,
          },
        }
      })
      .addCase(changeBlockPages, (state, action) =>
        isEqual(state.blockPages.data, state.blockPages.snapshot)
          ? {
              ...state,
              blockPages: {
                ...state.blockPages,
                snapshot: state.blockPages.data,
                data: action.payload,
              },
              settingReverted: false,
            }
          : {
              ...state,
              blockPages: {...state.blockPages, data: action.payload},
            },
      )
      .addCase(removeBlockPagesSnapshot, state =>
        !isEqual(state.blockPages.data, state.blockPages.snapshot)
          ? {
              ...state,
              blockPages: {
                ...state.blockPages,
                data: state.blockPages.snapshot,
                updatedData: {},
              },
              settingReverted: true,
            }
          : {
              ...state,
            },
      )
      .addCase(defaultBlockPagesUpdate, state => ({
        ...state,
        updateDefaultBlockPagesStatus: SliceStatus.pending,
      }))
      .addCase(defaultBlockPagesUpdateSuccess, state => ({
        ...state,
        updateDefaultBlockPagesStatus: SliceStatus.resolved,
        defaultBlockPagePayload: {} as DefaultBlockPagePayload,
        blockPages: {
          ...state.blockPages,
          snapshot: state.blockPages.data,
        },
      }))
      .addCase(defaultBlockPagesUpdateFailure, state => ({
        ...state,
        updateDefaultBlockPagesStatus: SliceStatus.rejected,
      }))
      .addCase(resetDefaultBlockPagesUpdate, state => ({
        ...state,
        updateDefaultBlockPagesStatus: SliceStatus.idle,
      }))
      .addCase(customBlockPagesUpdate, state => ({
        ...state,
        updateCustomBlockPageStatus: SliceStatus.pending,
      }))
      .addCase(customBlockPagesUpdateFailure, state => ({
        ...state,
        updateCustomBlockPageStatus: SliceStatus.rejected,
      }))
      .addCase(customBlockPagesUpdateSuccess, state => ({
        ...state,
        updateCustomBlockPageStatus: SliceStatus.resolved,
        blockPages: {
          ...state.blockPages,
          snapshot: state.blockPages.data,
          updatedData: {},
        },
      }))
      .addCase(resetCustomBlockPagesUpdate, state => ({
        ...state,
        updateCustomBlockPageStatus: SliceStatus.idle,
      }))
      .addCase(inviteAdmins, state => ({
        ...state,
        status: SliceStatus.pending,
      }))
      .addCase(inviteAdminsFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
      }))
      .addCase(inviteAdminsSuccess, state => ({
        ...state,
        status: SliceStatus.resolved,
      }))
      .addCase(updateInvitedAdmins, state => ({
        ...state,
        updateInvitedAdminsStatus: SliceStatus.pending,
      }))
      .addCase(updateInvitedAdminsFailure, state => ({
        ...state,
        updateInvitedAdminsStatus: SliceStatus.rejected,
      }))
      .addCase(updateInvitedAdminsSuccess, state => ({
        ...state,
        updateInvitedAdminsStatus: SliceStatus.resolved,
      }))
      .addCase(getRoles, state => ({
        ...state,
        rolesStatus: SliceStatus.pending,
      }))
      .addCase(getRolesFailure, state => ({
        ...state,
        rolesStatus: SliceStatus.rejected,
      }))
      .addCase(getRolesSuccess, (state, action) => ({
        ...state,
        rolesStatus: SliceStatus.resolved,
        availableRoles: action.payload,
      }))
      .addCase(deleteAdmin, state => ({
        ...state,
        status: SliceStatus.pending,
      }))
      .addCase(deleteAdminFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
      }))
      .addCase(deleteAdminSuccess, state => ({
        ...state,
        status: SliceStatus.resolved,
      }))
      .addCase(getEndpointsConfig, state => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getEndpointsConfigFailure, state => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(resetEndpointsConfig, state => ({
        ...state,
        endpointsConfig: {...endpointsConfigInitState},
      }))
      .addCase(getEndpointsConfigSuccess, (state, action) => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          data: action.payload,
          snapshot: action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(changeEndpointsConfig, (state, action) => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          data: action.payload,
        },
      }))
      .addCase(setEndpointConfigFormValidation, (state, action) => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          isFormValid: action.payload,
        },
      }))
      .addCase(setShowEndpointConfigView, (state, action) => ({
        ...state,
        endpointsConfig: {
          ...state.endpointsConfig,
          showConfigView: action.payload,
        },
      }))
      .addCase(updateEndpointsConfig, state => ({
        ...state,
        status: SliceStatus.pending,
        // reset the form validity on update API call submit.
        endpointsConfig: {
          ...state.endpointsConfig,
          isFormValid: false,
        },
      }))
      .addCase(updateEndpointsConfigFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
      }))
      .addCase(updateEndpointsConfigSuccess, state => ({
        ...state,
        status: SliceStatus.resolved,
        endpointsConfig: {
          ...state.endpointsConfig,
          snapshot: state.endpointsConfig.data,
        },
      }))
      .addCase(removeEndpointsConfigSnapshot, state =>
        !isEqual(state.endpointsConfig.data, state.endpointsConfig.snapshot)
          ? {
              ...state,
              endpointsConfig: {
                ...state.endpointsConfig,
                data: state.endpointsConfig.snapshot,
              },
            }
          : {
              ...state,
            },
      )
      .addCase(updateCustomBlockPageData, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        blockPages: {
          ...state.blockPages,
          updatedData: action.payload,
        },
      }))
      .addCase(getGroups, state => ({
        ...state,
        status: SliceStatus.pending,
        groups: {
          ...state.groups,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getGroupsFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
        groups: {
          ...state.groups,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(getGroupsSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        groups: {
          ...action.payload,
          Items: [
            ...state?.groups?.Items,
            ...(action.payload?.body?.Items ?? []),
          ],
          LastEvaluatedKey: action.payload?.body?.LastEvaluatedKey ?? {},
          hasMore:
            action.payload.body &&
            'LastEvaluatedKey' in action.payload.body &&
            action.payload.body.LastEvaluatedKey !== null,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(getUsers, state => ({
        ...state,
        status: SliceStatus.pending,
        users: {
          ...state.users,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getUsersFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
        users: {
          ...state.users,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(getUsersSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        users: {
          message: action.payload.message,
          LastEvaluatedKey: action.payload?.body?.LastEvaluatedKey ?? {},
          Items: [
            ...state?.users?.Items,
            ...(action.payload?.body?.Items ?? []),
          ],
          hasMore:
            action.payload.body &&
            'LastEvaluatedKey' in action.payload.body &&
            action.payload.body.LastEvaluatedKey !== null,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(resetUsersGroups, state => ({
        ...state,
        users: usersListInitialState,
        groups: groupsListInitialState,
      }))
      .addCase(updateDefaultBlockPagePayload, (state, action) => ({
        ...state,
        defaultBlockPagePayload: action.payload,
      }))
      .addCase(setShowUserGroupDetails, (state, action) => ({
        ...state,
        userGroupDetails: {
          ...userGroupDetailsInitState,
          ...action.payload,
        },
      }))
      .addCase(getUserGroupDetails, state => ({
        ...state,
        userGroupDetails: {
          ...state.userGroupDetails,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getUserGroupDetailsSuccess, (state, action) => ({
        ...state,
        userGroupDetails: {
          ...state.userGroupDetails,
          ...action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(getUserGroupDetailsFailure, (state, action) => ({
        ...state,
        userGroupDetails: {
          ...state.userGroupDetails,
          status: SliceStatus.rejected,
          error: action?.payload,
        },
      }))
      .addCase(updateSettingField, (state, action) => ({
        ...state,
        updatedSettingField: {
          ...state.updatedSettingField,
          ...action.payload,
        },
      }))
      .addCase(resetSettingUpdateField, state => ({
        ...state,
        updatedSettingField: {
          value: '',
          throwError: false,
        },
        settingReverted: false,
      }))
      .addCase(formSubmitted, (state, action) => ({
        ...state,
        formSubmitted: action.payload,
      }))
      .addCase(savePressed, (state, action) => ({
        ...state,
        savePressed: action.payload,
      }))
      .addCase(updateSliceStatus, (state, action) => ({
        ...state,
        status: action.payload,
      }))
      .addDefaultCase(state => state),
})

export const {reducer: settingsReducer, name: settingsReducerName} =
  settingsSlice

export type TSettingsActions =
  | ReturnType<typeof getLink>
  | ReturnType<typeof getLinkFailure>
  | ReturnType<typeof getLinkSuccess>
  | ReturnType<typeof getImportStatus>
  | ReturnType<typeof getImportStatusFailure>
  | ReturnType<typeof getImportStatusSuccess>
  | ReturnType<typeof resetImportStatus>
  | ReturnType<typeof getGroupsUsers>
  | ReturnType<typeof getGroupsUsersFailure>
  | ReturnType<typeof getGroupsUsersSuccess>
  | ReturnType<typeof getSearchUsersGroups>
  | ReturnType<typeof getSearchUsersGroupsFailure>
  | ReturnType<typeof getSearchUsersGroupsSuccess>
  | ReturnType<typeof resetSearch>
  | ReturnType<typeof inviteAdmins>
  | ReturnType<typeof inviteAdminsFailure>
  | ReturnType<typeof inviteAdminsSuccess>
  | ReturnType<typeof deleteAdmin>
  | ReturnType<typeof deleteAdminFailure>
  | ReturnType<typeof deleteAdminSuccess>
  | ReturnType<typeof getBlockPagesSuccess>
  | ReturnType<typeof changeBlockPages>
  | ReturnType<typeof removeBlockPagesSnapshot>
  | ReturnType<typeof defaultBlockPagesUpdate>
  | ReturnType<typeof defaultBlockPagesUpdateFailure>
  | ReturnType<typeof defaultBlockPagesUpdateSuccess>
  | ReturnType<typeof resetDefaultBlockPagesUpdate>
  | ReturnType<typeof getEndpointsConfig>
  | ReturnType<typeof getEndpointsConfigFailure>
  | ReturnType<typeof getEndpointsConfigSuccess>
  | ReturnType<typeof updateEndpointsConfig>
  | ReturnType<typeof updateEndpointsConfigFailure>
  | ReturnType<typeof updateEndpointsConfigSuccess>
  | ReturnType<typeof setEndpointConfigFormValidation>
  | ReturnType<typeof setShowEndpointConfigView>
  | ReturnType<typeof changeEndpointsConfig>
  | ReturnType<typeof removeEndpointsConfigSnapshot>
  | ReturnType<typeof updateCustomBlockPageData>
  | ReturnType<typeof removeUpdatedCustomBlockPageData>
  | ReturnType<typeof customBlockPagesUpdate>
  | ReturnType<typeof customBlockPagesUpdateFailure>
  | ReturnType<typeof customBlockPagesUpdateSuccess>
  | ReturnType<typeof resetCustomBlockPagesUpdate>
  | ReturnType<typeof startPollingGroupsUsers>
  | ReturnType<typeof stopPollingGroupsUsers>
  | ReturnType<typeof getGroups>
  | ReturnType<typeof getGroupsFailure>
  | ReturnType<typeof getGroupsSuccess>
  | ReturnType<typeof getUsers>
  | ReturnType<typeof getUsersFailure>
  | ReturnType<typeof getUsersSuccess>
  | ReturnType<typeof resetUsersGroups>
  | ReturnType<typeof getUserGroupDetails>
  | ReturnType<typeof getUserGroupDetailsSuccess>
  | ReturnType<typeof getUserGroupDetailsFailure>
  | ReturnType<typeof setShowUserGroupDetails>
  | ReturnType<typeof updateInvitedAdmins>
  | ReturnType<typeof updateInvitedAdminsFailure>
  | ReturnType<typeof updateInvitedAdminsSuccess>
  | ReturnType<typeof getRoles>
  | ReturnType<typeof getRolesFailure>
  | ReturnType<typeof getRolesSuccess>
  | ReturnType<typeof updateSliceStatus>
  | ReturnType<typeof updateSettingField>
  | ReturnType<typeof resetSettingUpdateField>
  | ReturnType<typeof formSubmitted>
  | ReturnType<typeof savePressed>

export const settingsActions = {
  getLink,
  getLinkFailure,
  getLinkSuccess,
  getGroupsUsers,
  getGroupsUsersFailure,
  getGroupsUsersSuccess,
  getSearchUsersGroups,
  getSearchUsersGroupsFailure,
  getSearchUsersGroupsSuccess,
  resetSearch,
  inviteAdmins,
  inviteAdminsFailure,
  inviteAdminsSuccess,
  deleteAdmin,
  deleteAdminFailure,
  deleteAdminSuccess,
  getImportStatus,
  getImportStatusFailure,
  getImportStatusSuccess,
  resetImportStatus,
  getBlockPagesSuccess,
  changeBlockPages,
  removeBlockPagesSnapshot,
  defaultBlockPagesUpdate,
  defaultBlockPagesUpdateFailure,
  defaultBlockPagesUpdateSuccess,
  resetDefaultBlockPagesUpdate,
  getEndpointsConfig,
  getEndpointsConfigFailure,
  getEndpointsConfigSuccess,
  updateEndpointsConfig,
  updateEndpointsConfigFailure,
  updateEndpointsConfigSuccess,
  setEndpointConfigFormValidation,
  setShowEndpointConfigView,
  changeEndpointsConfig,
  removeEndpointsConfigSnapshot,
  removeUpdatedCustomBlockPageData,
  updateCustomBlockPageData,
  customBlockPagesUpdate,
  customBlockPagesUpdateFailure,
  customBlockPagesUpdateSuccess,
  resetCustomBlockPagesUpdate,
  startPollingGroupsUsers,
  stopPollingGroupsUsers,
  getGroups,
  getGroupsFailure,
  getGroupsSuccess,
  getUsers,
  getUsersFailure,
  getUsersSuccess,
  resetUsersGroups,
  updateDefaultBlockPagePayload,
  getUserGroupDetails,
  getUserGroupDetailsSuccess,
  getUserGroupDetailsFailure,
  setShowUserGroupDetails,
  updateInvitedAdmins,
  updateInvitedAdminsFailure,
  updateInvitedAdminsSuccess,
  getRoles,
  getRolesFailure,
  getRolesSuccess,
  updateSliceStatus,
  updateSettingField,
  resetSettingUpdateField,
  formSubmitted,
  savePressed,
}

export type SettingsState = ReturnType<typeof settingsReducer>
